import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Bell,
  Download,
  Calendar,
  Car,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  CreditCard,
  History,
  Filter
} from 'lucide-react';
import DashboardSwitcher from '../components/layout/DashboardSwitcher';
import OwnerTopNav from '../components/layout/OwnerTopNav';
import Sidebar from '../components/layout/Sidebar';
import { ownerService } from '../services/owner.service';

const formatCurrency = (amount) => `RF ${Math.round(amount || 0).toLocaleString()}`;

const Finances = () => {
  const [activeDateRange, setActiveDateRange] = useState('Last 30 Days');
  const [activeVehicle, setActiveVehicle] = useState('All Vehicles');
  const [activeStatus, setActiveStatus] = useState('All Statuses');
  const [searchQuery, setSearchQuery] = useState('');
  const [statsData, setStatsData] = useState({
    totalPaidOut: 0,
    pendingPayouts: 0,
    estimatedEarnings: 0,
    availableBalance: 0,
    nextPayoutDate: 'Calculating...'
  });

  const [stats, setStats] = useState([
    { label: 'TOTAL PAID OUT', amount: 'RF 0', change: '0% from last month', changeType: 'neutral', icon: <CreditCard className="w-5 h-5 text-green-600" />, iconBg: 'bg-green-100' },
    { label: 'PENDING PAYOUTS', amount: 'RF 0', change: 'Calculating...', changeType: 'info', icon: <Clock className="w-5 h-5 text-brand-black" />, iconBg: 'bg-brand-orange-100' },
    { label: 'ESTIMATED EARNINGS', amount: 'RF 0', change: 'Future earnings', changeType: 'neutral', icon: <History className="w-5 h-5 text-gray-600" />, iconBg: 'bg-gray-100' }
  ]);

  const [transactions, setTransactions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pageError, setPageError] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    fetchStats();
    fetchVehicles();
    fetchPayoutRequests();
    fetchSettings();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await ownerService.getFinanceStats();
      setStatsData(res);

      setStats([
        {
          label: 'TOTAL PAID OUT',
          amount: formatCurrency(res.totalPaidOut || 0),
          change: '+14.2% from last month',
          changeType: 'up',
          icon: <CreditCard className="w-5 h-5 text-green-600" />,
          iconBg: 'bg-green-100'
        },
        {
          label: 'PENDING PAYOUTS',
          amount: formatCurrency(res.pendingPayouts || 0),
          change: res.nextPayoutDate || 'Calculating...',
          changeType: 'info',
          icon: <Clock className="w-5 h-5 text-brand-black" />,
          iconBg: 'bg-brand-orange-100'
        },
        {
          label: 'ESTIMATED EARNINGS',
          amount: formatCurrency(res.estimatedEarnings || 0),
          change: 'Projected Earnings',
          changeType: 'neutral',
          icon: <History className="w-5 h-5 text-gray-600" />,
          iconBg: 'bg-gray-100'
        }
      ]);
    } catch (err) {
      console.error("Failed to fetch finance stats", err);
      setPageError(err.message || 'Failed to load finance stats.');
    }
  };

  const fetchVehicles = async () => {
    try {
      const res = await ownerService.getOwnerFleet();
      setVehicles(res);
    } catch (err) {
      console.error("Failed to fetch vehicles", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await ownerService.getSettings();
      setSettings(res);
    } catch (err) {
      console.error("Failed to fetch payout settings", err);
    }
  };

  const fetchPayoutRequests = async () => {
    try {
      const res = await ownerService.getPayoutRequests();
      setPayoutRequests(res);
    } catch (err) {
      console.error("Failed to fetch payout requests", err);
    }
  };

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        size: 10,
        carId: activeVehicle !== 'All Vehicles' ? activeVehicle : undefined,
        status: activeStatus !== 'All Statuses' ? activeStatus.toUpperCase() : undefined,
        // search: searchQuery || undefined, // Backend doesn't support search in transactions yet, but could be added
      };

      const res = await ownerService.getTransactions(params);
      setTransactions(res.content);
      setTotalPages(res.totalPages);
      setTotalElements(res.totalElements);
    } catch (err) {
      console.error("Failed to fetch transactions", err);
    } finally {
      setLoading(false);
    }
  }, [page, activeVehicle, activeStatus]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-600 text-[10px] font-black uppercase tracking-widest">
            <CheckCircle2 className="w-3 h-3" />
            Paid
          </span>
        );
      case 'Processing':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-orange-50 text-brand-black text-[10px] font-black uppercase tracking-widest">
            <Clock className="w-3 h-3" />
            Processing
          </span>
        );
      case 'Pending':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-black uppercase tracking-widest">
            <AlertCircle className="w-3 h-3" />
            Pending
          </span>
        );
      case 'Failed':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest">
            <X className="w-3 h-3" />
            Failed
          </span>
        );
      case 'Cancelled':
        return (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-widest">
            <X className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const handleRequestPayout = async () => {
    try {
      setRequestingPayout(true);
      setPageError('');
      await ownerService.createPayoutRequest({
        amount: Number(payoutAmount),
        note: payoutNote
      });
      setPayoutModalOpen(false);
      setPayoutAmount('');
      setPayoutNote('');
      await Promise.all([fetchStats(), fetchPayoutRequests()]);
    } catch (err) {
      setPageError(err.message || 'Failed to create payout request.');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleExportPayouts = async () => {
    try {
      setPageError('');
      const blob = await ownerService.exportPayoutRequests();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'owner-payout-requests.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setPageError(err.message || 'Failed to export payout requests.');
    }
  };

  const payoutAccountLabel = settings?.payout
    ? `${settings.payout.bankName} • ${settings.payout.accountName} • ${settings.payout.accountNumber}`
    : 'No payout account saved';

  const canRequestPayout = Boolean(settings?.payout?.bankId) && Number(statsData.availableBalance || 0) > 0;

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
      <Sidebar activePage="Financials" />

      <main className="flex-grow overflow-y-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 flex items-center justify-between px-10 py-5 sticky top-0 z-20">
          <div className="relative w-96">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-transparent rounded-xl text-sm font-medium focus:bg-white focus:ring-2 focus:ring-brand-orange-500 transition-all outline-none"
            />
          </div>

          <div className="flex items-center gap-6">
            <button className="p-2.5 text-gray-400 bg-gray-50 rounded-xl hover:text-brand-black transition-all relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <div className="w-10 h-10 rounded-xl bg-brand-black text-white flex items-center justify-center font-black text-xs">
              AS
            </div>
          </div>
        </header>

        <div className="p-10">
          <div className="mb-4"><DashboardSwitcher active="/owner-dashboard"/></div>
          <OwnerTopNav activePage="Financials"/>
          {pageError && (
            <div className="mb-6 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
              {pageError}
            </div>
          )}

          {/* Top Bar */}
          <div className="flex items-end justify-between mb-10">
            <div>
              <h1 className="text-4xl font-black tracking-tight mb-2 uppercase">Payment History</h1>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Review your past earnings and manage upcoming payouts for your luxury fleet.</p>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={handleExportPayouts}
                className="flex items-center gap-2 px-6 py-3.5 bg-white border border-gray-100 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
              <button
                onClick={() => setPayoutModalOpen(true)}
                disabled={!canRequestPayout}
                className="flex items-center gap-2 px-6 py-3.5 bg-brand-black text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="w-4 h-4" />
                Request Payout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {stats.map((stat, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{stat.label}</p>
                  <div className={`p-3 rounded-xl ${stat.iconBg} group-hover:scale-110 transition-transform`}>
                    {stat.icon}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="text-3xl font-black tracking-tight">{stat.amount}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {stat.changeType === 'up' && <TrendingUp className="w-3.5 h-3.5 text-green-500" />}
                    <p className={`text-[10px] font-bold ${stat.changeType === 'up' ? 'text-green-500' : 'text-gray-400'}`}>
                      {stat.change}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 mb-10">
            <section className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black tracking-tight uppercase">Request Payout</h2>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Send available earnings to your saved payout account.</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Available Balance</p>
                  <p className="text-2xl font-black mt-2">{formatCurrency(statsData.availableBalance)}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl bg-gray-50 px-5 py-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Payout Account</p>
                  <p className="text-sm font-black">{payoutAccountLabel}</p>
                </div>
                <div className="rounded-2xl bg-gray-50 px-5 py-4">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Pending Review</p>
                  <p className="text-sm font-black">{formatCurrency(statsData.pendingPayouts)}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{statsData.nextPayoutDate}</p>
                </div>
              </div>
            </section>

            <section className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-black tracking-tight uppercase">Recent Payout Requests</h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Track the status of your latest payout requests.</p>
              </div>
              <div className="space-y-4">
                {payoutRequests.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 px-5 py-8 text-center text-[10px] font-black uppercase tracking-widest text-gray-400">
                    No payout requests yet
                  </div>
                ) : payoutRequests.map((request) => (
                  <div key={request.id} className="rounded-2xl border border-gray-100 px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-black">{request.amount}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{request.referenceNumber}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">{request.bankName} • {request.accountNumber}</p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-3">Requested {request.requestedAt}</p>
                    {request.note && (
                      <p className="text-xs font-medium text-gray-500 mt-2">{request.note}</p>
                    )}
                    {request.rejectionReason && (
                      <p className="text-xs font-medium text-red-600 mt-2">{request.rejectionReason}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Filters Bar */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm mb-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Date Range</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={activeDateRange}
                    onChange={(e) => { setActiveDateRange(e.target.value); setPage(0); }}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-xs font-bold appearance-none cursor-pointer focus:ring-2 focus:ring-brand-orange-500 transition-all outline-none"
                  >
                    <option>Last 30 Days</option>
                    <option>Last 90 Days</option>
                    <option>Year to Date</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Vehicle</label>
                <div className="relative">
                  <Car className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={activeVehicle}
                    onChange={(e) => { setActiveVehicle(e.target.value); setPage(0); }}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-xs font-bold appearance-none cursor-pointer focus:ring-2 focus:ring-brand-orange-500 transition-all outline-none"
                  >
                    <option>All Vehicles</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Status</label>
                <div className="relative">
                  <CheckCircle2 className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={activeStatus}
                    onChange={(e) => { setActiveStatus(e.target.value); setPage(0); }}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-xs font-bold appearance-none cursor-pointer focus:ring-2 focus:ring-brand-orange-500 transition-all outline-none"
                  >
                    <option>All Statuses</option>
                    <option>Paid</option>
                    <option>Pending</option>
                    <option>Processing</option>
                    <option>Failed</option>
                  </select>
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setActiveVehicle('All Vehicles');
                    setActiveStatus('All Statuses');
                    setActiveDateRange('Last 30 Days');
                    setPage(0);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-400 hover:bg-gray-100 transition-all"
                >
                  <Filter className="w-4 h-4" />
                  Reset Filters
                </button>
              </div>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50 bg-gray-50/30">
                  <th className="text-left py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase">Date</th>
                  <th className="text-left py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase">Transaction ID</th>
                  <th className="text-left py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase">Vehicle</th>
                  <th className="text-center py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase">Amount</th>
                  <th className="text-center py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase">Status</th>
                  <th className="text-right py-6 px-10 text-[10px] font-black uppercase tracking-widest text-gray-400 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="py-20 text-center text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading transactions...</td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-20 text-center text-gray-400 font-bold uppercase tracking-widest text-[10px]">No transactions found</td>
                  </tr>
                ) : transactions.map((trx, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="py-6 px-10">
                      <div>
                        <p className="text-sm font-black">{trx.date}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{trx.time}</p>
                      </div>
                    </td>
                    <td className="py-6 px-10 text-xs font-bold text-gray-400 tracking-tight">
                      {trx.id}
                    </td>
                    <td className="py-6 px-10">
                      <div className="flex items-center gap-4">
                        <img src={trx.image || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&q=80&w=100"} alt={trx.vehicle} className="w-10 h-10 rounded-xl object-cover shadow-sm" />
                        <div>
                          <p className="text-sm font-black">{trx.vehicle}</p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{trx.vehicleId} • {trx.type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-6 px-10">
                      <div className="text-center">
                        <span className={`text-sm font-black ${trx.amount.startsWith('-') ? 'text-gray-900' : 'text-green-600'}`}>
                          {trx.amount}
                        </span>
                      </div>
                    </td>
                    <td className="py-6 px-10">
                      <div className="flex justify-center">
                        {getStatusBadge(trx.status)}
                      </div>
                    </td>
                    <td className="py-6 px-10 text-right">
                      <button className="p-2 text-gray-300 hover:text-brand-black transition-all">
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="p-8 border-t border-gray-50 flex items-center justify-between">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Showing {transactions.length} of {totalElements} transactions
              </p>
              <div className="flex items-center gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  className={`p-2 rounded-lg transition-all ${page === 0 ? 'text-gray-200 cursor-not-allowed' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                  <span className="text-gray-900 px-3 py-1 bg-gray-50 rounded-lg text-[10px] font-black uppercase">Page {page + 1} of {totalPages || 1}</span>
                </div>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                  className={`p-2 rounded-lg transition-all ${page >= totalPages - 1 ? 'text-gray-200 cursor-not-allowed' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {payoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-black uppercase tracking-tight">Request Payout</h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Available: {formatCurrency(statsData.availableBalance)}</p>
              </div>
              <button onClick={() => setPayoutModalOpen(false)} className="p-2 text-gray-400 hover:text-brand-black">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="rounded-2xl bg-gray-50 px-5 py-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Payout Account</p>
                <p className="text-sm font-black">{payoutAccountLabel}</p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Amount</label>
                <input
                  type="number"
                  min="1"
                  max={Math.floor(statsData.availableBalance || 0)}
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="w-full rounded-xl bg-gray-50 px-5 py-3.5 text-sm font-bold outline-none transition-all focus:bg-white focus:ring-2 focus:ring-[#111111]"
                  placeholder="Enter amount in RF"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Note</label>
                <textarea
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl bg-gray-50 px-5 py-3.5 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-2 focus:ring-[#111111]"
                  placeholder="Optional note for the payout request"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => setPayoutModalOpen(false)}
                className="rounded-xl border border-gray-200 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestPayout}
                disabled={requestingPayout || !canRequestPayout || !Number(payoutAmount)}
                className="rounded-xl bg-brand-black px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white hover:bg-black disabled:opacity-50"
              >
                {requestingPayout ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finances;
